'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './QualityToolsCarousel.module.scss';
import gsap from 'gsap';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface ToolItem {
  label: string;
  tagline: string;
  image: string;
}

interface QualityToolsCarouselProps {
  label?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  toolsDescription?: string;
  tools?: ToolItem[];
  tilt?: 'left' | 'right';
  backgroundImage?: string;
}

const DEFAULT_TOOLS: ToolItem[] = [
  {
    label: 'Snyk',
    tagline: 'Sécurité des dépendances et vulnérabilités',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(1).png',
  },
  {
    label: 'Lighthouse',
    tagline: 'Performance, accessibilité, SEO et bonnes pratiques web',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(8).png',
  },
  {
    label: 'CodeClimate',
    tagline: 'Qualimétrie continue et dette technique',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(6).png',
  },
  {
    label: 'Cypress',
    tagline: 'Tests end-to-end fiables et rapides',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(4).png',
  },
  {
    label: 'Codeguard',
    tagline: 'Sauvegarde et surveillance du site',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(2).png',
  },
  {
    label: 'Dependabot',
    tagline: 'Mises à jour automatiques des dépendances',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(7).png',
  },
  {
    label: 'Jest',
    tagline: 'Tests unitaires pour du code robuste',
    image:
      'https://ik.imagekit.io/lindahestia/articles/ChatGPT%20Image%2016%20jun%202026,%2004_48_55%20p.m.%20(5).png',
  },
];

const DEFAULT_BG =
  'https://ik.imagekit.io/lindahestia/articles/software_quality_sketch_under_100kb.webp';

const getSlot = (index: number, active: number, length: number): number => {
  let diff = index - active;
  const half = Math.floor(length / 2);
  if (diff < -half) {
    diff += length;
  }
  if (diff > half) {
    diff -= length;
  }
  return diff;
};

export function QualityToolsCarousel({
  label = 'Qualité enterprise',
  title = 'Notre contrat de',
  highlightedTitle = 'qualité',
  description = 'Chaque livrable est soumis à nos standards techniques exigeants.',
  toolsDescription = 'Ces outils sont intégrés à notre workflow pour garantir qualité, transparence et métriques accessibles à chaque étape.',
  tools = DEFAULT_TOOLS,
  tilt = 'right',
  backgroundImage = DEFAULT_BG,
}: QualityToolsCarouselProps) {
  useAOS();
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  /* Animate the word "qualité" */
  useEffect(() => {
    if (!sectionRef.current) {
      return;
    }
    const el = sectionRef.current.querySelector('[data-quality-reveal]');
    if (!el) {
      return;
    }
    gsap.set(el, { opacity: 0, y: 16, scale: 0.92 });
    gsap.to(el, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.35,
      ease: 'back.out(1.6)',
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top 80%',
        end: '+=30%',
        scrub: 0.5,
      },
    });
  }, []);

  /* Auto-advance every 6s, pause on hover/focus/touch */
  useEffect(() => {
    if (tools.length <= 1 || paused) {
      return;
    }
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % tools.length);
    }, 6000);
    return () => clearInterval(id);
  }, [paused, tools.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + tools.length) % tools.length);
  }, [tools.length]);

  const goNext = useCallback(() => {
    setActiveIndex((i) => (i + 1) % tools.length);
  }, [tools.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        gaClick('quality_tools_prev');
        goPrev();
      } else if (e.key === 'ArrowRight') {
        gaClick('quality_tools_next');
        goNext();
      }
    },
    [goPrev, goNext]
  );

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;

  return (
    <section
      ref={sectionRef}
      className={`${styles.section} ${tiltClass}`}
      aria-labelledby="quality-title"
      style={{ '--quality-bg-image': `url("${backgroundImage}")` } as React.CSSProperties}
    >
      <div className={styles.animatedBg} aria-hidden="true" />
      <div className={styles.diagonalBanner} aria-hidden="true" />

      <div className={styles.content}>
        {label && <p className={styles.sectionLabel}>{label}</p>}
        <h2 id="quality-title" className={styles.title}>
          {title}{' '}
          <em>
            <span data-quality-reveal>{highlightedTitle}</span>
          </em>
        </h2>
        <p className={styles.description}>{description}</p>

        {toolsDescription && <p className={styles.toolsDescription}>{toolsDescription}</p>}

        {/* 3D circular carousel */}
        <div className={styles.carousel}>
          {/* WAI-ARIA carousel pattern: focusable region with arrow-key navigation */}
          {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          <div
            className={styles.scene}
            role="region"
            aria-roledescription="carousel"
            aria-label="Outils qualité"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
          >
            {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
            <button
              type="button"
              className={`${styles.navButton} ${styles.navButtonLeft}`}
              onClick={() => {
                gaClick('quality_tools_prev');
                goPrev();
              }}
              aria-label="Outil précédent"
            >
              <ChevronLeft size={24} />
            </button>

            <div className={styles.track}>
              {tools.map((tool, i) => {
                const slot = getSlot(i, activeIndex, tools.length);
                const isHidden = Math.abs(slot) > 2;
                const slotClass = isHidden
                  ? styles.hidden
                  : slot === 0
                    ? styles.slot0
                    : slot === 1
                      ? styles.slot1
                      : slot === -1
                        ? styles.slotNeg1
                        : slot === 2
                          ? styles.slot2
                          : styles.slotNeg2;

                return (
                  <div
                    key={tool.label}
                    className={`${styles.card} ${slotClass}`}
                    style={{ backgroundImage: `url("${tool.image}")` }}
                    onClick={() => {
                      gaClick('quality_tools_card', { tool_label: tool.label });
                      setActiveIndex(i);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        gaClick('quality_tools_card', { tool_label: tool.label });
                        setActiveIndex(i);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${tool.label} ${tool.tagline}`}
                  >
                    <div className={styles.cardOverlay} aria-hidden="true" />
                    <div className={styles.cardContent} data-card-content>
                      <span className={styles.cardLabel}>{tool.label}</span>
                      <span className={styles.cardTagline}>{tool.tagline}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.mobileDots} aria-label="Choisir un outil">
              {tools.map((tool, idx) => (
                <button
                  key={`${tool.label}-dot`}
                  type="button"
                  className={`${styles.mobileDot} ${idx === activeIndex ? styles.mobileDotActive : ''}`}
                  onClick={() => {
                    gaClick('quality_tools_dot', { tool_label: tools[idx].label });
                    setActiveIndex(idx);
                  }}
                  aria-label={`Afficher ${tool.label}`}
                  aria-current={idx === activeIndex ? 'true' : undefined}
                />
              ))}
            </div>

            <button
              type="button"
              className={`${styles.navButton} ${styles.navButtonRight}`}
              onClick={() => {
                gaClick('quality_tools_next');
                goNext();
              }}
              aria-label="Outil suivant"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
